import React, { ReactElement, useEffect, useState } from 'react';
import { Component as BoxIcon } from 'icons/icon-box--diversified.svg';
import { Component as CaretDownIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUpIcon } from 'icons/icon-ui--caret-up-outline.svg';
import MealKitIcon from 'icons/icon-circle--meal-kit.svg';
import RteIcon from 'icons/icon-circle--ready-to-eat.svg';
import DiversifiedIcon from 'icons/icon-circle--mix-and-match.svg';
import customizeIcon from 'consumer/signup/plans/customize_icon.svg';
import Plan, { PlanType, PlanName, Programs } from 'models/Plan';
import {
  DiversifiedPlanGroup,
  PLAN_TYPE_TO_GROUP_NAME,
  PlanGroupModel,
} from 'models/DiversifiedPlanGroup';
import AppliedCoupon from 'models/AppliedCoupon';
import useAsyncEffect from 'hooks/useAsyncEffect';
import { get } from 'utils/network';
import IPlanInfo from 'models/IPlanInfo';
import { ApiRoute } from 'config';
import DiversifiedGoalToggle from '../SignUp/DiversifiedPlan/DiversifiedGoalToggle';
import { formatDateToShort } from '../../utils/dateTimes';
import SelectableServingQuantity from '../SignUp/SelectPlan/SelectableServingQuantity';
import ItemizedList from '../shared/ItemizedList';
import SelectableImageTile from '../shared/SelectableImageTile';
import SelectableExtrasQuantity from '../SignUp/SelectPlan/SelectableExtrasQuantity';
import PriceBreakdown from './PriceBreakdown';
import {
  DEFAULT_MEAL_KIT_EXTRAS,
  DEFAULT_PREPARED_EXTRAS,
  DEFAULT_NO_EXTRAS,
  DEFAULT_SERVINGS,
  LIST_ITEMS_BY_PLAN_TYPE,
} from '../UpdatePlan/utils/constants';
import PlanTypeHeader from './PlanTypeHeader';

interface Props {
  selectedPlan: Plan;
  menuShippingDate: string;
  discount: AppliedCoupon;
  onPlanChange: (plan: Plan) => void;
  loggedIn?: boolean;
}

function DiversifiedOrderSummary({
  selectedPlan,
  discount,
  menuShippingDate,
  onPlanChange,
  loggedIn = false,
}: Props): ReactElement {
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [isPlanInitialized, setIsPlanInitialized] = useState(Plan.isInitialized);
  const [selectedMealKitServings, setSelectedMealKitServings] = useState(DEFAULT_SERVINGS);
  const [selectedMealKitExtras, setSelectedMealKitExtras] = useState(DEFAULT_MEAL_KIT_EXTRAS);
  const [selectedPreparedExtras, setSelectedPreparedExtras] = useState(DEFAULT_PREPARED_EXTRAS);

  const CaretIcon = showOrderSummary ? CaretUpIcon : CaretDownIcon;
  const toggleShowOrderSummary = () => setShowOrderSummary(!showOrderSummary);
  const showServingsToggle = ['chefs_choice', 'four_servings'].includes(selectedPlan.name) && selectedPlan.type !== PlanType.DIVERSIFIED;
  const showExtrasToggle = [PlanType.MEAL_KIT, PlanType.PREPARED].includes(selectedPlan.type);

  const handleSelectPlan = (planName: PlanName, isDiversified = false) => {
    let newPlan: Plan;
    switch (planName) {
      case 'jumpstart_program':
        newPlan = Plan.getPlan(1, DEFAULT_NO_EXTRAS, Programs.JUMPSTART_PROGRAM);
        break;
      case 'prepared_one_serving':
        newPlan = Plan.getPlan(1, selectedPreparedExtras);
        break;
      case 'four_servings':
        setSelectedMealKitServings(4);
        newPlan = Plan.getPlan(4, selectedMealKitExtras, planName);
        break;
      default:
        setSelectedMealKitServings(2);
        newPlan = Plan.getPlan(
          2, isDiversified ? DEFAULT_NO_EXTRAS : selectedMealKitExtras, planName, isDiversified,
        );
    }

    onPlanChange(newPlan);
  };

  // only "chef's choice" gives the option of 2 vs. 4 servings (4 servings = 'four-servings' plan)
  const handleSelectMealKitServings = (servings: number) => {
    let newPlan = selectedPlan;
    if (servings === 4) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras);
    } else if (servings === 2) {
      newPlan = Plan.getPlan(servings, selectedMealKitExtras, 'chefs_choice');
    }

    setSelectedMealKitServings(servings);
    onPlanChange(newPlan);
  };

  const handleSelectExtras = (extraDinners: number) => {
    const selectedExtras = { dinner: extraDinners, breakfast: 0, lunch: 0 };
    const newPlan = Plan.getPlan(selectedPlan.servings, selectedExtras, selectedPlan.name);
    if (newPlan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitExtras(selectedExtras);
    } else {
      setSelectedPreparedExtras(selectedExtras);
    }

    onPlanChange(newPlan);
  };

  useEffect(() => {
    if (selectedPlan.type === PlanType.MEAL_KIT) {
      setSelectedMealKitServings(selectedPlan.servings);
      setSelectedMealKitExtras(selectedPlan.selectedExtras);
    } else if (selectedPlan.type === PlanType.PREPARED) {
      setSelectedPreparedExtras(selectedPlan.selectedExtras);
    }
  }, [selectedPlan]);

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS_SHORT);
      // @ts-expect-error TODO: fix null safety
      Plan.initialize(response.parsedBody);
      if (isMounted) setIsPlanInitialized(true);
    }
  }, []);

  return (
    <div className="c-order-summary-panel c-signup-order-summary c-signup-checkout__aside">
      <div className="c-sticky-widget" style={{ boxShadow: '0px' }}>
        <PlanTypeHeader
          selectedPlan={selectedPlan}
          discount={discount}
        />
        <div className="shipping-info">
          <span className="shipping-icon"><BoxIcon /></span>
          <span>{`Ships on ${formatDateToShort(menuShippingDate)}`}</span>
        </div>
      </div>

      <div className="c-update-plan__plans-container">
        <SelectableImageTile
          label="Meal Kit"
          value={PlanType.MEAL_KIT}
          isSelected={selectedPlan.type === PlanType.MEAL_KIT}
          imageUrl={MealKitIcon}
          onClick={() => selectedPlan.type !== PlanType.MEAL_KIT
            && handleSelectPlan('chefs_choice', false)}
        />
        <SelectableImageTile
          label="Ready-to-Eat"
          value={PlanType.PREPARED}
          isSelected={[PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type)}
          imageUrl={RteIcon}
          onClick={() => ![PlanType.PREPARED, PlanType.PROGRAMS].includes(selectedPlan.type)
            && handleSelectPlan('prepared_one_serving', false)}
        />
        <SelectableImageTile
          label="Mix & Match"
          value={PlanType.DIVERSIFIED}
          isSelected={selectedPlan.type === PlanType.DIVERSIFIED}
          imageUrl={DiversifiedIcon}
          onClick={() => selectedPlan.type !== PlanType.DIVERSIFIED
            && handleSelectPlan('chefs_choice', true)}
        />
      </div>

      <hr className="c-signup-order-summary__break" />

      <DiversifiedGoalToggle
        plans={DiversifiedPlanGroup[PLAN_TYPE_TO_GROUP_NAME[selectedPlan.type]].plans}
        selectedPlan={selectedPlan.name}
        onSelect={(planName: PlanName) => selectedPlan.name !== planName
          && handleSelectPlan(planName, selectedPlan.isDiversified)}
      />
      <hr />

      <div
        className="order-summary-breakdown-container"
        onClick={toggleShowOrderSummary}
        role="button"
        onKeyPress={toggleShowOrderSummary}
        tabIndex={0}
      >
        <div className="order-summary-breakdown-content">
          <span>Order Summary</span>
          <CaretIcon className="c-svg-fill-primary-color" width="15" focusable="false" />
        </div>
      </div>

      {!showOrderSummary && <hr />}

      {showOrderSummary && (
        <div className="c-update-plan__servings-container">

          {showServingsToggle ? (
            <>
              <hr />
              <SelectableServingQuantity
                planType={selectedPlan.type}
                selectedServings={selectedMealKitServings}
                handleSelectServings={handleSelectMealKitServings}
                planName={selectedPlan.name}
              />
            </>
          ) : (
            <ItemizedList
              list={LIST_ITEMS_BY_PLAN_TYPE[selectedPlan.type]}
              planName={selectedPlan.name}
            />
          )}

          {showExtrasToggle && (
            <>
              <hr />
              <SelectableExtrasQuantity
                plan={selectedPlan}
                handleSelectExtras={handleSelectExtras}
                showMostPopularWidget={false}
              />
              <hr />
            </>
          )}

          {!showServingsToggle && !showExtrasToggle && (<hr />)}
        </div>
      )}

      <div className="customize-row">
        <img src={customizeIcon} alt="customize-icon" />
        <span>Complete Signup to choose your meals.</span>
      </div>

      {isPlanInitialized && (
        <PriceBreakdown
          plan={selectedPlan}
          loggedIn={loggedIn}
        />
      )}
    </div>
  );
}

export default DiversifiedOrderSummary;
